import I18n from 'i18n-js';
import React, { useContext, useState } from 'react';
import { keys, filter, includes } from 'lodash';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCLoadingBars, RCText, RCTag } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';

import { SellFormContext } from '../../SellFormContext';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';
import {
  carrierCalculatedPackageValidator,
  oversizeGuitarPackageWarningKey,
  isOversizedDimensionsLabel,
  REGULAR_SIZED_LONGEST_EDGE_THRESHOLD,
  OVERSIZED_LONGEST_EDGE_THRESHOLD,
} from './carrierCalculatedPackageValidator';
import { Field } from '../../sellFormReducerTypes';

export default function CarrierCalculatedGuitarEstimator() {
  const { listingEntry, shopConfig } = useContext(SellFormContext);
  const [shippingEstimatesResponse, setShippingEstimatesResponse] = useState([]);

  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const validPackageEntry = packageEntryComplete(packageEntry) && !carrierCalculatedPackageValidator(packageEntry)?.errorTranslationKey;
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const shouldFetchEstimate = shopConfig.currencySetting === 'USD' &&
                                validPackageEntry &&
                                !!currentPriceInput &&
                                currentPriceCents > 0;
  const isOversizedDimensions = oversizeGuitarPackageWarningKey(packageEntry) === isOversizedDimensionsLabel;
  const overrideComparisonPackageDimension = () => {
    if (!shouldFetchEstimate) return {};
    const longestSideKey = filter(keys(packageEntry), (key) => includes(['length', 'width', 'height'], key)).sort((a, b) => packageEntry[b]?.value - packageEntry[a]?.value)[0];

    return { [longestSideKey]: { value: `${isOversizedDimensions ? REGULAR_SIZED_LONGEST_EDGE_THRESHOLD : OVERSIZED_LONGEST_EDGE_THRESHOLD}`, unit: 'in' } };
  };
  const userInputPackageDetails = {
    packageEntry,
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };
  const comparisonPackageDetails = {
    packageEntry: {
      ...packageEntry,
      ...overrideComparisonPackageDimension(),
    },
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };

  const { loading } = useQuery(
    FETCH_SHIPPING_ESTIMATES,
    {
      skip: !shouldFetchEstimate,
      variables: {
        input: {
          packageDetails: [
            userInputPackageDetails,
            comparisonPackageDetails,
          ],
        },
      },
      onCompleted: ({ shippingEstimates }) => setShippingEstimatesResponse(shippingEstimates.estimates),
    },
  );

  const estimateAvailable = !loading && shippingEstimatesResponse?.[0]?.priceLow?.display;
  const userInputEstimate = shippingEstimatesResponse?.[0];
  const comparisonEstimate = shippingEstimatesResponse?.[1];

  return (
    <>
      {loading && validPackageEntry && (
        <div className="d-flex fx-align-center fx-justify-center mt-6 mb-2">
          <RCLoadingBars />
        </div>
      )}

      {estimateAvailable && validPackageEntry && (
        <div className="d-flex fx-dir-col fx-align-start gap-4 mt-6">
          <RCText.Utility size="400" weight="bold">
            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHintV2')}
          </RCText.Utility>
          <div className="carrier-calculated-guitar-estimates">
            <div className="carrier-calculated-guitar-shipping-estimator">
              <RCText.Utility size="350">
                {I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'oversizedBoxDimensions' : 'standardBoxDimensions' }`)}
              </RCText.Utility>
              <RCText.Utility size="500" weight="bold">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                  lowDisplay: userInputEstimate.priceLow.display,
                  highDisplay: userInputEstimate.priceHigh.display,
                })}
              </RCText.Utility>
              <RCTag variant={`${isOversizedDimensions ? 'default' : 'highlight' }`}>
                <span className="tt-upercase">{I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'oversizedBoxSizeTag' : 'standardBoxSizeTag' }`)}</span>
              </RCTag>
            </div>
            <div className="carrier-calculated-guitar-shipping-estimator">
              <RCText.Utility size="350">
                {I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'standardBoxDimensionsTip' : 'oversizedBoxDimensionsTip' }`)}
              </RCText.Utility>
              <RCText.Utility size="400" weight="semibold">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                  lowDisplay: comparisonEstimate.priceLow.display,
                  highDisplay: comparisonEstimate.priceHigh.display,
                })}
              </RCText.Utility>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export const FETCH_SHIPPING_ESTIMATES = gql(`
  query Core_SellForm_FetchShippingEstimates($input: InputShippingEstimatesRequest!) {
    shippingEstimates(input: $input) {
      estimates {
        priceLow {
          amountCents
          display
        }
        priceHigh {
          amountCents
          display
        }
      }
    }
  },
`);
